import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const DebitNoteList = ({ onTabChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [debitNotes, setDebitNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [downloadDropdown, setDownloadDropdown] = useState(null);
  const [printDropdown, setPrintDropdown] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch debit notes
  const fetchDebitNotes = async () => {
    try {
      setLoading(true);
      console.log('Fetching debit notes...');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(partnerFilter && { partnerId: partnerFilter }),
        ...(companyFilter && { companyId: companyFilter })
      });

      const token = localStorage.getItem('jwtToken');
      console.log('Token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`/api/finance/debit-notes?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(t('debitNoteList.failedToFetchDebitNotes'));
      }

      const data = await response.json();
      console.log('Debit notes data:', data);
      setDebitNotes(data.debitNotes);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching debit notes:', error);
      setError(t('debitNoteList.failedToFetchDebitNotes'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch partners for filter
  const fetchPartners = async () => {
    try {
      // Fetch partners for filter - only active partners
      const response = await fetch('/api/finance/partners?activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteList.failedToFetchPartners'));
      }

      const data = await response.json();
      setPartners(data.partners || data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch companies for filter
  const fetchCompanies = async () => {
    try {
      const response = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteList.failedToFetchCompanies'));
      }

      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Handle download
  const handleDownload = async (debitNoteId, debitNoteNumber, debitNoteType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}/pdf?type=${debitNoteType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${debitNoteNumber}-${debitNoteType}.pdf`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setSuccessMessage(t('debitNoteList.downloadSuccess', { type: debitNoteType }));
        setDownloadDropdown(null);
      } else {
        throw new Error(t('debitNoteList.failedToDownload'));
      }
    } catch (error) {
      console.error('Error downloading debit note as PDF:', error);
      setError(t('debitNoteList.failedToDownload'));
    }
  };

  const handlePrint = async (debitNoteId, debitNoteNumber, debitNoteType = 'ORIGINAL') => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}/pdf?type=${debitNoteType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open PDF in new tab for preview and printing
        const newWindow = window.open(url, '_blank');
        
        // Clean up URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
        
        setPrintDropdown(null);
      } else {
        throw new Error(t('debitNoteList.failedToLoadForPrinting'));
      }
    } catch (error) {
      console.error('Error printing debit note:', error);
      setError(t('debitNoteList.failedToPrint'));
    }
  };

  // Handle delete
  const handleDelete = async (debitNoteId) => {
    if (!window.confirm(t('debitNoteList.confirmDelete'))) {
      return;
    }

    try {
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteList.failedToDelete'));
      }

      setSuccessMessage(t('debitNoteList.deleteSuccess'));
      fetchDebitNotes();
    } catch (error) {
      console.error('Error deleting debit note:', error);
      setError(t('debitNoteList.failedToDelete'));
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { bg: 'bg-gray-300', text: 'text-gray-800' },
      'Sent': { bg: 'bg-blue-100', text: 'text-blue-800' },
      'Paid': { bg: 'bg-green-100', text: 'text-green-800' },
      'Partial': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      'Overdue': { bg: 'bg-red-100', text: 'text-red-800' },
      'Cancelled': { bg: 'bg-gray-100', text: 'text-gray-600' }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {t(`debitNoteList.status${status}`)}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN'
    }).format(amount);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPartnerFilter('');
    setCompanyFilter('');
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchDebitNotes();
  }, [currentPage, searchTerm, statusFilter, partnerFilter, companyFilter]);

  useEffect(() => {
    fetchPartners();
    fetchCompanies();
  }, []);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setDownloadDropdown(null);
        setPrintDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto">
              {/* Hero Section */}
        <div className="flex items-center justify-between mb-6 px-4 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('debitNoteList.title')}</h1>
          <p className="text-gray-600">{t('debitNoteList.subtitle')}</p>
        </div>
        <button 
          onClick={() => onTabChange('debit-note-create')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>{t('debitNoteList.newDebitNote')}</span>
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {successMessage}
          <button 
            onClick={() => setSuccessMessage('')}
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
          <button 
            onClick={() => setError('')}
            className="float-right font-bold"
          >
            ×
          </button>
        </div>
      )}

              {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 mx-4 sm:mx-6 lg:mx-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('debitNoteList.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('debitNoteList.allStatuses')}</option>
            <option value="Draft">{t('debitNoteList.statusDraft')}</option>
            <option value="Sent">{t('debitNoteList.statusSent')}</option>
            <option value="Partial">{t('debitNoteList.statusPartial')}</option>
            <option value="Paid">{t('debitNoteList.statusPaid')}</option>
            <option value="Overdue">{t('debitNoteList.statusOverdue')}</option>
            <option value="Cancelled">{t('debitNoteList.statusCancelled')}</option>
          </select>

          {/* Partner Filter */}
          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('debitNoteList.allPartners')}</option>
            {partners.map((partner) => (
              <option key={partner._id} value={partner._id}>
                {partner.name}
              </option>
            ))}
          </select>

          {/* Company Filter */}
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('debitNoteList.allCompanies')}</option>
            {companies.map((company) => (
              <option key={company._id} value={company._id}>
                {company.name}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            {t('debitNoteList.clearFilters')}
          </button>
        </div>
      </div>

      {/* Debit Notes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <span className="text-sm text-gray-600">
            {total} {total === 1 ? t('debitNoteList.debitNote') : t('debitNoteList.debitNotes')} {t('debitNoteList.found')}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.debitNote')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {t('debitNoteList.originalInvoice')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.partner')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.issueDate')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('debitNoteList.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {debitNotes.map((debitNote) => (
                <tr key={debitNote._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {debitNote.debitNoteNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {debitNote.originalInvoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {debitNote.partnerId?.name || t('debitNoteList.notAvailable')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(debitNote.issueDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(debitNote.totalAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(debitNote.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {/* View */}
                      <button
                        onClick={() => navigate(`/admin/dashboard/debit-notes/${debitNote._id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('debitNoteList.viewDetails')}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>

                      {/* Download Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setDownloadDropdown(downloadDropdown === debitNote._id ? null : debitNote._id)}
                          className="text-green-600 hover:text-green-900"
                          title={t('debitNoteList.download')}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </button>
                        {downloadDropdown === debitNote._id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                            <div className="py-1">
                              <button
                                onClick={() => handleDownload(debitNote._id, debitNote.debitNoteNumber, 'ORIGINAL')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {t('debitNoteList.downloadOriginal')}
                              </button>
                              <button
                                onClick={() => handleDownload(debitNote._id, debitNote.debitNoteNumber, 'COPY')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {t('debitNoteList.downloadCopy')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Print Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setPrintDropdown(printDropdown === debitNote._id ? null : debitNote._id)}
                          className="text-purple-600 hover:text-purple-900"
                          title={t('debitNoteList.print')}
                        >
                          <PrinterIcon className="h-4 w-4" />
                        </button>
                        {printDropdown === debitNote._id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                            <div className="py-1">
                              <button
                                onClick={() => handlePrint(debitNote._id, debitNote.debitNoteNumber, 'ORIGINAL')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {t('debitNoteList.printOriginal')}
                              </button>
                              <button
                                onClick={() => handlePrint(debitNote._id, debitNote.debitNoteNumber, 'COPY')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {t('debitNoteList.printCopy')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Edit */}
                      <button
                        onClick={() => onTabChange('debit-note-edit', { id: debitNote._id })}
                        className="text-indigo-600 hover:text-indigo-900"
                        title={t('debitNoteList.edit')}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(debitNote._id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('debitNoteList.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('debitNoteList.previous')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('debitNoteList.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('debitNoteList.showingPage')} <span className="font-medium">{currentPage}</span> {t('debitNoteList.of')}{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('debitNoteList.previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('debitNoteList.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Empty State */}
      {debitNotes.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('debitNoteList.noDebitNotesFound')}</p>
        </div>
      )}
    </div>
  );
};

export default DebitNoteList; 